import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import { AuthService } from "@/services/authService";
import { currentUser } from "@/redux/reducers/AuthReducer";
import { resolveLandingPath } from "@/routes/landing";

export const GithubIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" className="shrink-0">
    <path
      fill="currentColor"
      d="M12 .5C5.65.5.5 5.65.5 12c0 5.09 3.29 9.4 7.86 10.93.58.11.79-.25.79-.56
         0-.28-.01-1.02-.02-2-3.2.7-3.88-1.54-3.88-1.54-.53-1.35-1.3-1.71-1.3-1.71-1.06-.73.08-.72.08-.72
         1.17.08 1.79 1.2 1.79 1.2 1.04 1.78 2.73 1.27 3.4.97.11-.76.41-1.27.75-1.56-2.56-.29-5.26-1.28-5.26-5.69
         0-1.26.45-2.29 1.19-3.1-.12-.29-.52-1.45.11-3.02 0 0 .98-.31 3.2 1.18a11.1 11.1 0 0 1 2.91-.39c.99 0 1.99.13 2.91.39
         2.22-1.49 3.2-1.18 3.2-1.18.63 1.57.23 2.73.11 3.02.74.81 1.19 1.84 1.19 3.1
         0 4.43-2.71 5.39-5.29 5.67.42.36.8 1.08.8 2.18 0 1.57-.02 2.84-.02 3.23 0 .31.21.68.8.56A10.52 10.52 0 0 0 23.5 12
         C23.5 5.65 18.35.5 12 .5Z"
    />
  </svg>
);

const GithubCallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");

    const storedState = localStorage.getItem("oauth_state");
    localStorage.removeItem("oauth_state");

    if (!code || !state) {
      toast.error("GitHub sign in was cancelled or failed.");
      navigate("/login?error=oauth_failed");
      return;
    }

    if (!storedState) {
      toast.error("Session expired. Please try signing in again.");
      navigate("/login?error=oauth_failed");
      return;
    }

    AuthService.handleGithubCallback(code, state, storedState)
      .then(async (data) => {
        const result = await dispatch(currentUser());

        if (currentUser.fulfilled.match(result)) {
          toast.success(`Welcome, ${data.user?.name || data.user?.email || "there"}!`);
          // Resolve the destination from the account that just signed in.
          // /admin/dashboard is wrong for a SuperAdmin (RouteGuard bounces them
          // to /super-admin/*, showing the tenant shell on the way) and wrong
          // for anyone whose role lacks dashboard.view — they would land on
          // "Unauthorized" straight after a successful sign-in.
          navigate(resolveLandingPath(result.payload?.user), { replace: true });
        } else {
          toast.error("Failed to load user profile. Please try again.");
          navigate("/login?error=oauth_failed");
        }
      })
      .catch((err) => {
        console.error("GitHub callback error:", err?.response?.data);
        const msg = err?.response?.data?.message || "GitHub sign in failed. Please try again.";
        toast.error(msg);
        navigate("/login?error=oauth_failed");
      });
  }, [dispatch, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#111111] text-white">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="animate-spin text-indigo-400" size={28} />
        <p className="text-sm text-white/60">Completing GitHub sign in...</p>
      </div>
    </div>
  );
};

export default GithubCallback;
